/**
 * Site preloader.
 *
 * Holds the cover until the page has loaded, then hands over to the entrance
 * animations with goodface:preloader-done. A minimum on screen keeps a fast
 * load from flashing the cover, and a hard ceiling means a stalled asset can
 * never keep the page hidden.
 */

import { DURATION, reducedMotion } from './utils/motion';

const MIN_VISIBLE = 400;
// Well under the 4s failsafe in reveal.scss, and short enough that the cover
// never becomes the reason the page's largest paint is late.
const MAX_VISIBLE = 1500;

const preloader = document.querySelector<HTMLElement>('[data-preloader]');

if (preloader) {
  const root = document.documentElement;
  const started = Date.now();
  let done = false;

  // The lock takes the scrollbar away, which would widen the page by its width
  // and shift everything sideways the moment the cover leaves. Measured before
  // locking and handed to the CSS, which gives the same width back as padding.
  const scrollbar = window.innerWidth - root.clientWidth;

  if (scrollbar > 0) {
    root.style.setProperty('--gf-scrollbar', `${scrollbar}px`);
  }

  // Announces itself so the CSS failsafe stands down and the scroll lock, which
  // only this script can release, applies.
  root.classList.add('has-preloader-js', 'is-preloading');

  const finish = (): void => {
    if (done) {
      return;
    }

    done = true;

    root.classList.remove('is-preloading');
    root.style.removeProperty('--gf-scrollbar');
    preloader.classList.add('is-done');
    // Read by reveal.ts when it runs after the event has already fired.
    preloader.setAttribute('data-preloader-done', '');
    document.dispatchEvent(new CustomEvent('goodface:preloader-done'));

    // Out of the way once it has faded, so nothing under it stays untouchable.
    window.setTimeout(
      () => preloader.remove(),
      reducedMotion() ? 0 : DURATION.slow
    );
  };

  const release = (): void => {
    const wait = reducedMotion() ? 0 : Math.max(0, MIN_VISIBLE - (Date.now() - started));

    window.setTimeout(finish, wait);
  };

  /**
   * Ready enough to uncover: the markup is parsed and the picture the page is
   * judged on has arrived. Deliberately *not* window.load — that waits for
   * every script, iframe and tracker on the page, and holding the cover for a
   * tag manager is how a preloader turns into the site's slowest moment.
   */
  const ready = (): void => {
    const hero = document.querySelector<HTMLImageElement>(
      'img[fetchpriority="high"], .hero__media-item'
    );

    if (!hero || hero.complete) {
      release();
      return;
    }

    hero.addEventListener('load', release, { once: true });
    hero.addEventListener('error', release, { once: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready, { once: true });
  } else {
    ready();
  }

  window.setTimeout(finish, MAX_VISIBLE);
}

export {};
